// İSG Portal — Araç kaydı (ana firma / web-expert) — api-contract §23.1, `[ModuleAction("contractors", …)]`.
// Taşeron `{uuid}` çağıranın firmasında olmalı (aksi `404 CONTRACTOR_NOT_FOUND`). İzin ekranı bu uçları
// iznin `contractorUuid`'iyle çağırır. `vehicleUuid` her yerde KAYIT aracıdır (§23.0.1); belge silme/
// onay/red hedefi `VehicleDocumentDto.uuid`'dir (§8 `documentUuid` DEĞİL). Listeler sayfalanmaz.

import { api } from './client';
import type { Empty } from './envelope';
import type {
  VehicleDocumentDto,
  VehicleDocumentRejectRequest,
  VehicleDocumentUploadRequest,
  VehicleWriteRequest,
} from './permits';

/**
 * Taşeronun kayıt aracı — §23.0.7 (`ContractorVehicleDto`). Kilit olguları rol-nötrdür; rol kararı
 * istemcidedir (`canEditPlate`/`vehicleDeleteBlock`, `permits/vehicleRegistry.ts`). Hazırlık alanı YOKTUR
 * (hazırlık izin tipine göredir). `documents` = aracın tüm canlı belgeleri (oluşturulma azalan).
 */
export interface ContractorVehicleDto {
  uuid: string;
  plate: string;
  description: string | null;
  /** En az bir izin bağı var ya da oldu (kaldırılmışlar dahil). */
  hasBeenLinked: boolean;
  /** Saklanan statüsü `Closed`/`Rejected` olmayan bir izne canlı bağ var → araç DELETE `409`. */
  hasOpenPermits: boolean;
  /** Silinmiş dahil `Approved` belge var → kimse plaka anahtarını değiştiremez. */
  isPlateLocked: boolean;
  documents: VehicleDocumentDto[];
  createdAt: string | null;
}

const base = (contractorUuid: string) => `contractors/${contractorUuid}/vehicles`;

/** `GET /contractors/{uuid}/vehicles` (`get`). */
export function listContractorVehicles(contractorUuid: string) {
  return api.get<ContractorVehicleDto[]>(base(contractorUuid));
}

/** `POST /contractors/{uuid}/vehicles` (`update`) → `201`. Anahtar varsa `409 VEHICLE_PLATE_ALREADY_EXISTS`. */
export function createContractorVehicle(contractorUuid: string, body: VehicleWriteRequest) {
  return api.post<ContractorVehicleDto>(base(contractorUuid), { json: body });
}

/**
 * `PUT /contractors/{uuid}/vehicles/{vehicleUuid}` (`update`) — tam-replace (`description` verilmezse
 * temizlenir). `409 VEHICLE_PLATE_LOCKED` / `VEHICLE_PLATE_ALREADY_EXISTS`.
 */
export function updateContractorVehicle(
  contractorUuid: string,
  vehicleUuid: string,
  body: VehicleWriteRequest,
) {
  return api.put<ContractorVehicleDto>(`${base(contractorUuid)}/${vehicleUuid}`, { json: body });
}

/** `DELETE /contractors/{uuid}/vehicles/{vehicleUuid}` (`update`) — açık izinde `409 VEHICLE_HAS_OPEN_PERMITS`. */
export function deleteContractorVehicle(contractorUuid: string, vehicleUuid: string) {
  return api.delete<Empty>(`${base(contractorUuid)}/${vehicleUuid}`);
}

/** `POST …/vehicles/{vehicleUuid}/documents` (`update`) → `201`, belge `Pending` başlar. */
export function linkVehicleDocument(
  contractorUuid: string,
  vehicleUuid: string,
  body: VehicleDocumentUploadRequest,
) {
  return api.post<VehicleDocumentDto>(`${base(contractorUuid)}/${vehicleUuid}/documents`, {
    json: body,
  });
}

/** `DELETE …/documents/{did}` (`update`) — `409 VEHICLE_DOCUMENT_IN_USE`. `did` = `VehicleDocumentDto.uuid`. */
export function deleteVehicleDocument(
  contractorUuid: string,
  vehicleUuid: string,
  documentUuid: string,
) {
  return api.delete<Empty>(`${base(contractorUuid)}/${vehicleUuid}/documents/${documentUuid}`);
}

/** `POST …/documents/{did}/approve` (`update`) — idempotent, her durumdan; izin gerekmez. */
export function approveVehicleDocument(
  contractorUuid: string,
  vehicleUuid: string,
  documentUuid: string,
) {
  return api.post<VehicleDocumentDto>(
    `${base(contractorUuid)}/${vehicleUuid}/documents/${documentUuid}/approve`,
  );
}

/** `POST …/documents/{did}/reject {reason}` (`update`) — `reason` zorunlu ≤ 500; idempotent, her durumdan. */
export function rejectVehicleDocument(
  contractorUuid: string,
  vehicleUuid: string,
  documentUuid: string,
  body: VehicleDocumentRejectRequest,
) {
  return api.post<VehicleDocumentDto>(
    `${base(contractorUuid)}/${vehicleUuid}/documents/${documentUuid}/reject`,
    { json: body },
  );
}
